// The theme picks, as the MODDING panel and the theme workshop change them
// (themes.md §2, §8, §11, R814).
//
// This is the one module that writes a theme pick or a theme code. It keeps
// three things in step: the settings blob (`ui/settings.ts`), the local
// caches of `ui/lookcache.ts`, and the account's theme links on the meta
// server. Every reader asks `currentLook()` and never reads a pick itself.
//
// The rules it holds:
//   - A vendored pick sends `links: {<category>: null}`, so the meta server
//     knows the account no longer uses a custom theme there. A CUSTOM pick
//     with a stored code sends the code again.
//   - A link failure is silent. The settings are the client's truth; a link
//     only keeps a shared theme from the prune.
//   - `ensureThemeBodies` refreshes each custom body once per page load, and
//     only while the account holds `ui_themes`: a lapsed account keeps its
//     cache, and the lookup route would refuse it anyway (R809).
//   - A theme is found by its code AND its category (R819): one creator's
//     themes share one code, so every lookup names the category it wants.

import { KEY_UI_THEMES, THEME_CATEGORIES, type ThemeCategory } from '../gen/balance';
import {
  ApiError, CODE_ENTITLEMENT, CODE_NOT_FOUND, CODE_RATE_LIMITED, CODE_UNKNOWN_CODE,
  getTheme, putThemeLinks, type ThemeRecord,
} from '../net/api';
import {
  CUSTOM, DEFAULT_SETTINGS, VENDORED_UI_IDS, loadSettings, normalizeThemeCode, saveSettings,
  type GameSettings, type ThemePick,
} from './settings';
import {
  cachedEntitlements, cachedTheme, cachedThemes, markThemeMissing, pruneThemes, storeTheme,
  themeKey,
} from './lookcache';
import { PICK_FIELD, resolveLook, storedThemePick, type Look } from './look';
import { applyToSound, audioChoices } from './audiochoices';
import { THEME_CHOICES, applyTheme, applyThemeBody, resolveTheme, type ThemeChoice } from './theme';
import { wearLightFaction, wearLightTheme } from './lightchrome';
import type { AudioSettings } from '../audio/director';

// --- reading ----------------------------------------------------------------

/** The look the stored picks resolve to now. */
export function currentLook(s: GameSettings = loadSettings()): Look {
  const a = audioChoices();
  return resolveLook({
    settings: s,
    held: cachedEntitlements(),
    themes: cachedThemes(),
    audio: a.available ? a : null,
  });
}

// The pick a category holds in the settings. The resolver owns the rule.
export { storedThemePick };

// --- the change notification ------------------------------------------------

const listeners = new Set<(look: Look) => void>();

/** Calls `fn` with the new look after every pick, code or body change.
 *  The answer removes the listener. */
export function onLookChange(fn: (look: Look) => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

function notify(s: GameSettings): void {
  const look = currentLook(s);
  for (const fn of [...listeners]) fn(look);
}

// --- writing ----------------------------------------------------------------

/** One pick written into a copy of the settings. */
function withPick(s: GameSettings, c: ThemeCategory, pick: string): GameSettings {
  const next = { ...s, codes: { ...s.codes } };
  if (c !== 'ui') {
    next[PICK_FIELD[c]] = pick;
    return next;
  }
  // The UI pick is typed: a text that names no choice is AUTO.
  const choice = pick === CUSTOM || VENDORED_UI_IDS.includes(pick)
    ? pick
    : (THEME_CHOICES as readonly ThemeChoice[]).find((t) => String(t) === pick) ?? 'auto';
  next.theme = choice as ThemePick;
  return next;
}

/** Writes the settings, hands them to the sound director and notifies. */
function commit(next: GameSettings): void {
  saveSettings(next);
  applyToSound(next);
  notify(next);
}

/** Sends one link change and swallows every failure. */
function sendLink(c: ThemeCategory, code: string | null): void {
  void putThemeLinks({ [c]: code }).catch(() => { /* the settings stand */ });
}

/**
 * Sets one category's pick: a vendored id (`auto`, a faction number as
 * text, a playlist or SFX theme id, '' for the manifest default) or
 * `custom`. It answers the settings it wrote. The resolver judges the
 * entitlement, so a pick the account cannot use is stored and suspended.
 */
export function setThemePick(category: ThemeCategory, pick: string): GameSettings {
  const next = withPick(loadSettings(), category, pick);
  commit(next);
  if (pick !== CUSTOM) sendLink(category, null);
  else if (next.codes[category]) sendLink(category, next.codes[category]);
  return next;
}

/** The cache keys of the codes the settings name, one per category. */
function storedKeys(s: GameSettings): string[] {
  return THEME_CATEGORIES.filter((c) => s.codes[c] !== '').map((c) => themeKey(s.codes[c], c));
}

/**
 * Writes one category's code ('' for none) and its pick, prunes the cache
 * to the codes the settings still name, and sends the link: the code, or
 * its removal for ''. It answers the settings it wrote.
 */
function writeCode(category: ThemeCategory, code: string, pick: string): GameSettings {
  const s = loadSettings();
  const next = withPick({ ...s, codes: { ...s.codes, [category]: code } }, category, pick);
  commit(next);
  pruneThemes(storedKeys(next));
  sendLink(category, code || null);
  return next;
}

/**
 * Removes the code of one category (themes.md §2.1, R819): the code leaves
 * the settings, the link goes, and the pick goes back to the category's
 * default vendored value, which is AUTO. It needs no key, like the removal
 * of a link (R809). It answers the settings it wrote.
 */
export function unsetThemeCode(category: ThemeCategory): GameSettings {
  return writeCode(category, '', storedThemePick(DEFAULT_SETTINGS, category));
}

/** Why a theme code was refused. A surface maps the reason to its words.
 *  A code with no theme of the category is `not_found` (R819). */
export type ThemeCodeRefusal =
  | 'malformed' | 'not_found'
  | 'entitlement' | 'rate_limited' | 'unavailable' | 'failed';

/** A refused lookup. `message` is the server's sentence, where it sent one. */
export class ThemeCodeError extends Error {
  constructor(readonly reason: ThemeCodeRefusal, message: string = reason) {
    super(message);
    this.name = 'ThemeCodeError';
  }
}

/** Turns a failed route into a refusal. */
function refusal(err: unknown): ThemeCodeError {
  if (!(err instanceof ApiError)) return new ThemeCodeError('failed', String(err));
  if (err.unavailable) return new ThemeCodeError('unavailable', err.message);
  switch (err.code) {
    case CODE_NOT_FOUND:
    case CODE_UNKNOWN_CODE: return new ThemeCodeError('not_found', err.message);
    case CODE_ENTITLEMENT: return new ThemeCodeError('entitlement', err.message);
    case CODE_RATE_LIMITED: return new ThemeCodeError('rate_limited', err.message);
    default: return new ThemeCodeError('failed', err.message);
  }
}

/**
 * Looks up the theme one code names in one category and caches it. It
 * throws a `ThemeCodeError`: `malformed` before any request, else the
 * server's answer. A 404 for a cached pair marks the entry missing and
 * keeps it.
 */
export async function lookupTheme(raw: string, category: ThemeCategory): Promise<ThemeRecord> {
  const code = normalizeThemeCode(raw);
  if (!code) throw new ThemeCodeError('malformed');
  try {
    const t = await getTheme(code, category);
    storeTheme(t);
    return t;
  } catch (err) {
    const r = refusal(err);
    if (r.reason === 'not_found' && cachedTheme(code, category)) markThemeMissing(code, category);
    throw r;
  }
}

/**
 * Stores one theme code for a category and picks CUSTOM there. The pair is
 * looked up first, so a code with no theme of the category never reaches
 * the settings. The link follows the settings, and its failure is silent.
 * It answers the theme the pair names.
 */
export async function setThemeCode(category: ThemeCategory, raw: string): Promise<ThemeRecord> {
  const t = await lookupTheme(raw, category);
  writeCode(category, t.code, CUSTOM);
  return t;
}

/** The cache keys `ensureThemeBodies` refreshed in this page load. */
const refreshed = new Set<string>();

/**
 * Refreshes the custom bodies the stored picks name (themes.md §12). A
 * code that is not cached is fetched always; a cached one once per page
 * load. A 404 marks the entry missing and the resolver shows AUTO; any
 * other failure keeps the cache. Nothing is fetched without `ui_themes`.
 * It never rejects.
 */
export async function ensureThemeBodies(s: GameSettings = loadSettings()): Promise<void> {
  if (!cachedEntitlements().has(KEY_UI_THEMES)) return;
  const cache = cachedThemes();
  let changed = false;
  for (const c of THEME_CATEGORIES) {
    const code = s.codes[c];
    if (!code || storedThemePick(s, c) !== CUSTOM) continue;
    const key = themeKey(code, c);
    if (refreshed.has(key) && cache[key]) continue;
    refreshed.add(key);
    try {
      const t = await getTheme(code, c);
      const had = cache[key];
      if (!had || had.missing || had.version !== t.version) changed = true;
      storeTheme(t);
    } catch (err) {
      if (refusal(err).reason === 'not_found') {
        markThemeMissing(code, c);
        changed = true;
      }
    }
  }
  if (changed) notify(s);
}

/** True where a custom pick names a code with no cached body at all. */
export function missingThemeBodies(s: GameSettings = loadSettings()): boolean {
  const cache = cachedThemes();
  return THEME_CATEGORIES.some((c) =>
    storedThemePick(s, c) === CUSTOM && s.codes[c] !== '' && !cache[themeKey(s.codes[c], c)]);
}

/** Forgets the page-load memo. Tests only. */
export function resetThemeState(): void {
  refreshed.clear();
  listeners.clear();
}

// --- the readers' adapters --------------------------------------------------

/**
 * Writes the UI look onto the document root: the custom body where one is
 * in force, else the vendored choice, which AUTO resolves with `faction`.
 * `faction` is null before the account is known: AUTO then wears the
 * Abyssal Compact and keeps the stored ink of the next first paint (R1953).
 */
export function wearTheme(faction: number | null, look: Look = currentLook()): void {
  // A custom and a vendored UI theme each carry a body; a faction choice
  // does not. The game chrome of a map page in the light identity wears
  // the light body of the same theme (R3298); the page root stays dark.
  if (look.ui) {
    applyThemeBody(look.ui);
    wearLightTheme(look.ui);
  } else {
    applyTheme(look.picks.ui as ThemeChoice, faction ?? 0, faction !== null);
    wearLightFaction(resolveTheme(look.picks.ui as ThemeChoice, faction ?? 0));
  }
}

/**
 * The settings as the sound director takes them: the Music and the SFX
 * look resolved, and the entitlement set for the keyed manifest entries.
 */
export function soundSettings(s: GameSettings, look: Look = currentLook(s)): AudioSettings {
  const music = look.music;
  const sfx = look.sfx;
  return {
    musicOn: s.musicOn,
    sfxOn: s.sfxOn,
    audioTakeover: s.audioTakeover,
    musicVol: s.musicVol,
    sfxVol: s.sfxVol,
    playlist: look.picks.music === CUSTOM ? '' : look.picks.music,
    sfxTheme: look.picks.sfx === CUSTOM ? '' : look.picks.sfx,
    customPlaylist: music
      ? { id: `custom:${look.codes.music}`, title: look.codes.music ?? '', ...music }
      : null,
    customSfx: sfx
      ? { id: `custom:${look.codes.sfx}`, title: look.codes.sfx ?? '', map: sfx.map, voice: sfx.voice }
      : null,
    held: cachedEntitlements(),
  };
}
